'use strict';

import getVideoId from 'get-video-id';
import Player from '@vimeo/player';
import {
    youtube as youtubeOembed,
    vimeo as vimeoOembed,
    tiktok as tiktokOembed,
} from '../utils/oembed';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let youtubeApiPromise;

function loadYouTubeAPI() {
    if (youtubeApiPromise) {
        return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(resolve => { // eslint-disable-line compat/compat
        if (window.YT?.Player) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';

        const previous = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            resolve();
        }

        document.head.appendChild(script);
    });

    return youtubeApiPromise;
}

function createYouTubePlayer(url, id, oembed, autoplay = false) {
    let player;

    return {
        mount(container) {
            const createPlayer = () => {
                const dummy = document.createElement('div');
                dummy.id = `${container.id}-player`;
                container.appendChild(dummy);

                return new Promise(resolve => { // eslint-disable-line
                    player = new YT.Player(dummy.id, { // eslint-disable-line no-undef
                        videoId: id,
                        playerVars: {
                            autoplay: autoplay ? 1 : 0,
                            playlist: id,
                        },
                        events: {
                            onReady: resolve,
                        },
                    });
                });
            };

            // When the API is already available, call new YT.Player() synchronously
            // within the click handler. The iframe src is set synchronously with
            // autoplay=1, which is what iOS Safari requires for audio autoplay in
            // cross-origin iframes — postMessage-based play() calls do not qualify.
            if (window.YT?.Player) {
                return createPlayer();
            }

            return loadYouTubeAPI().then(createPlayer);
        },

        play() {
            player && player.playVideo();
        },

        pause() {
            player && player.pauseVideo();
        },

        stop() {
            player && player.pauseVideo();
            player && player.seekTo(0);
        },

        reset() {
            player && player.seekTo(0);
        },

        destroy() {
            player && player.destroy();
            player = null;
        },
    }
}

function createVimeoPlayer(url, id, oembed, autoplay = false) {
    let player;

    return {
        async mount(container) {
            return new Promise(resolve => { // eslint-disable-line
                const params = {
                    autoplay,
                    rel: 0,
                }

                if (new URL(url).searchParams.has('h')) {
                    params.url = url;
                } else {
                    params.id = id;
                }

                player = new Player(container, params);

                player.on('loaded', () => {
                    Promise.all([
                        player.getVideoTitle(),
                        player.getVideoWidth(),
                        player.getVideoHeight(),
                    ]).then(([ title, width, height ]) => {
                        resolve({
                            title,
                            width,
                            height,
                        });
                    });
                });
            });
        },

        play() {
            player && player.play();
        },

        pause() {
            player && player.pause();
        },

        stop() {
            player && player.pause().then(() => player.setCurrentTime(0));
        },

        reset() {
            player && player.setCurrentTime(0);
        },

        destroy() {
            player && player.destroy();
            player = null;
        },
    }
}

function createTikTokPlayer(url, id, oembed) {
    let iframe, origin;

    return {
        async mount(container) {
            return new Promise(async resolve => { // eslint-disable-line
                const data = await oembed();

                container.innerHTML = data.html;

                iframe = container.querySelector('iframe');
                origin = determineIframeOrigin(iframe);

                const listener = event => {
                    if (event.source !== iframe.contentWindow) {
                        return;
                    }

                    try {
                        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                        if (data.type === 'onPlayerReady') {
                            window.removeEventListener('message', listener);
                            resolve(iframe);
                        }
                    } catch (e) {
                        // ignore
                    }
                }

                window.addEventListener('message', listener);
            });
        },

        play() {
            iframe.contentWindow.postMessage({ type: 'play', 'x-tiktok-player': true }, origin);
        },

        pause() {
            iframe.contentWindow.postMessage({ type: 'pause', 'x-tiktok-player': true }, origin);
        },

        stop() {
            iframe.contentWindow.postMessage({ type: 'pause', 'x-tiktok-player': true }, origin);
            iframe.contentWindow.postMessage({ type: 'seekTo', value: 0, 'x-tiktok-player': true }, origin);
        },

        reset() {
            iframe.contentWindow.postMessage({ type: 'seekTo', value: 0, 'x-tiktok-player': true }, origin);
        },

        destroy() {
            iframe = null;
            origin = null;
        },
    }
}

function createHtml5Player(url, id, oembed, autoplay = false) {
    let video;

    return {
        async mount(container) {
            return new Promise(resolve => { // eslint-disable-line compat/compat
                video = document.createElement('video');
                video.src = url;
                video.controls = true;
                video.preload = 'metadata';
                video.autoplay = autoplay;
                video.playsInline = true;

                // Resolve once metadata is loaded so we know width/height
                video.addEventListener('loadedmetadata', () => {
                    resolve({
                        width: video.videoWidth,
                        height: video.videoHeight,
                    });
                }, { once: true });

                container.appendChild(video);
            });
        },

        play() {
            video && video.play();
        },

        pause() {
            video && video.pause();
        },

        stop() {
            video && video.pause();
            video && (video.currentTime = 0);
        },

        reset() {
            video && (video.currentTime = 0);
        },

        destroy() {
            video = null;
        },
    }
}

const PROVIDERS = {
    youtube: {
        createPlayer: createYouTubePlayer,
        oembed: youtubeOembed,
    },

    vimeo: {
        createPlayer: createVimeoPlayer,
        oembed: vimeoOembed,
    },

    tiktok: {
        createPlayer: createTikTokPlayer,
        oembed: (url, id) => {
            return tiktokOembed(url).then(response => {
                return {
                    ...response,
                    width: response.thumbnail_width,
                    height: response.thumbnail_height,
                    html: `<iframe width="${response.thumbnail_width}" height="${response.thumbnail_height}" src="https://www.tiktok.com/player/v1/${id}?rel=0&autoplay=0" allow="autoplay; encrypted-media" title="${response.title}"></iframe>`,
                }
            });
        },
    },

    html5: {
        createPlayer: createHtml5Player,
        oembed: (url, id) => Promise.resolve({}),
    },
}

const canPlayCache = {}

function canBrowserPlayMime(mime) {
    if (mime in canPlayCache) {
        return canPlayCache[mime];
    }

    const video = document.createElement('video');

    canPlayCache[mime] = video.canPlayType(mime) !== '';

    return canPlayCache[mime];
}

async function isDirectVideoUrl(url) {
    try {
        const response = await fetch(url, { // eslint-disable-line compat/compat
            method: 'HEAD',
            mode: 'cors',
        });

        if (!response.ok) {
            return false;
        }

        const contentType = response.headers.get('content-type');

        if (!contentType) {
            return false;
        }

        return canBrowserPlayMime(contentType.split(';')[0].trim());
    } catch (e) {
        return false;
    }
}

async function determineService(url) {
    // const isDirectVideo = url => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

    let { id, service } = getVideoId(url);

    // Added in a fallback since Basker hosted videos don't return true on the isDirectVideoUrl check.
    const directVideoExt = /\.(mp4|webm|ogg)(\?.*)?$/i;
    if (!service) {
        if (await isDirectVideoUrl(url)) {
            service = 'html5';
        } else if (directVideoExt.test(url)) {
            service = 'html5';
        }
    }

    let provider = PROVIDERS[service];

    if (!provider) {
        return null;
    }

    let oembedCache;

    return {
        id,
        service,
        oembed() {
            if (!oembedCache) {
                oembedCache = provider.oembed(url, id);
            }

            return oembedCache;
        },
        player(autoplay = false) {
            return provider.createPlayer(url, id, this.oembed, autoplay);
        },
    }
}

const determineIframeOrigin = iframe => {
    const a = document.createElement('a');
    a.href = iframe.src;
    return a.protocol + '//' + a.hostname + (a.port ? ':' + a.port : '');
}

const createDialog = provider => {
    const id = `video-modal-${provider.id}`;
    let dialog = document.getElementById(id);
    let heading, loader, media;

    if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = id;
        dialog.className = 'video-modal video-modal--loading';
        dialog.closedBy = 'any';
        dialog.setAttribute('aria-labelledby', `${dialog.id}-title`);

        heading = document.createElement('h2');
        heading.id = `${dialog.id}-title`;
        heading.className = 'video-modal__title';
        heading.textContent = `Loading ${provider.service} video`;

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'video-modal__close';
        closeButton.setAttribute('aria-label', 'Close video');
        closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>`;

        closeButton.addEventListener('click', () => {
            dialog.close();
        });

        loader = document.createElement('div');
        loader.className = 'video-modal__loader';
        loader.setAttribute('aria-label', 'Loading video');
        loader.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`;

        media = document.createElement('div');
        media.id = `${dialog.id}-media`;
        media.className = 'video-modal__media';

        const container = document.createElement('div');
        container.className = 'video-modal__container';
        container.append(closeButton, media);

        dialog.append(heading, loader, container);

        document.body.appendChild(dialog);
    } else {
        heading = dialog.querySelector(`#${dialog.id}-title`);
        loader = dialog.querySelector(`.video-modal__loader`);
        media = dialog.querySelector(`.video-modal__media`);
    }

    let player = null;
    let currentMount = null;
    let isSetup = false;

    const handleClose = () => {
        if (currentMount) {
            currentMount.then(() => {
                player && player.stop();
                dialog.classList.remove('video-modal--active');
            });
        } else {
            dialog.classList.remove('video-modal--active');
        }
    }

    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('cancel', handleClose);

    return {
        showModal: () => {
            if (player) {
                player.destroy?.();
                player = null;
            }
            media.innerHTML = '';

            // Restore loader if it was removed after a previous mount
            if (!loader.parentNode) {
                media.before(loader);
            }
            dialog.classList.add('video-modal--loading');
            dialog.classList.remove('video-modal--loaded', 'video-modal--active');

            dialog.showModal();

            // Create a fresh player with autoplay on every open. For YouTube,
            // new YT.Player() is called synchronously (when the API is available)
            // within the click handler, setting the iframe src with autoplay=1.
            // iOS Safari only allows audio autoplay in cross-origin iframes when
            // the iframe is created synchronously from a user gesture — postMessage
            // play() calls do not qualify regardless of when they are made.
            player = provider.player(!prefersReducedMotion);

            currentMount = player.mount(media).then(async video => {
                let oembed = null;

                if (provider.service === 'html5' || provider.service === 'vimeo') {
                    oembed = video;
                } else {
                    oembed = await provider.oembed();
                }

                if (!isSetup) {
                    if (provider.service === 'html5') {
                        heading.textContent = 'Video';
                    } else {
                        const title = document.createElement('span');
                        title.textContent = oembed.title;
                        heading.textContent = 'Video: ';
                        heading.appendChild(title);
                    }

                    const aspectRatio = oembed.width / oembed.height;
                    dialog.style.setProperty('--aspect-ratio', aspectRatio);

                    if (aspectRatio > 1) {
                        dialog.classList.add('video-modal--landscape');
                    } else if (aspectRatio < 1) {
                        dialog.classList.add('video-modal--portrait');
                    } else {
                        dialog.classList.add('video-modal--square');
                    }

                    isSetup = true;
                }

                loader.remove();
                dialog.classList.remove('video-modal--loading');
                dialog.classList.add('video-modal--loaded', 'video-modal--active');
            });
        },
    }
}

const SELECTOR_CLASS = 'js-video-modal';

export async function init() {
    const elements = document.querySelectorAll(`.${SELECTOR_CLASS}`);

    for (const element of elements) {
        const url = element.href;

        const provider = await determineService(url);

        if (!provider) {
            continue;
        }

        let dialog = null;

        element.addEventListener('click', event => {
            event.preventDefault();

            if (!dialog) {
                dialog = createDialog(provider);
            }

            dialog.showModal();
        })

        element.classList.add(`${SELECTOR_CLASS}--initialised`);
    }
}
